import { useEffect, useState, useCallback } from "react";
import { Table, Pagination, Icon, Button, Select } from "semantic-ui-react";
import { Loading } from "../../Loading/Loading";
import { CalendarInput } from "../../CalendarInput/CalendarInput";
import { formatDate } from "../../CalendarInput/formatDate";
import {
    calculationTypeOptions,
    logsFiltersInit,
    validationProcessFilterOptions,
    pageSizeOptions,
    statusOptions,
} from "../../../models/filters";
import { ITask } from "../../../models/task";
import { LogRow } from "./LogRow";
import { AppState } from "../../../store/configureStore";
import { useSelector } from "react-redux";
import { toast } from "../../..";
import { getCalculations } from "../../../services/calculationsService";
import "./Logs.css";
import { ValidationProcess } from "../../../store/validationProcess";

export const formatDateString = (date: string, showTime: boolean = true) => {
    if (date === "0001-01-01T00:00:00" || date === undefined || date === null) return "Running...";
    else if (showTime) {
        date = date.substring(0, date.indexOf("."));
        date = date.replace("T", " ");
        return date;
    } else return date.substring(0, date.indexOf("T"));
};

/*
const dropdownValueToIntraday = (value: number) => {
    if (value === -1) return null;
    return Boolean(value);
};

const intradayToDropdownValue = (value: boolean | null) => {
    if (value === null) return -1;
    return Number(value);
};
*/

export const Logs = () => {
    const users = useSelector((state: AppState) => state.users);
    const [logsFilters, setLogsFilters] = useState(logsFiltersInit);
    const [appliedLogsFilters, setAppliedLogsFilters] = useState(logsFiltersInit);
    const [logs, setLogs] = useState<ITask[]>([]);
    const [logsTotalCount, setLogsTotalCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loadingLogs, setLoadingLogs] = useState(false);

    const fetchCalculations = useCallback(
        async (useLoading = false) => {
            try {
                if (useLoading) setLoadingLogs(true);
                const res = await getCalculations(
                    pageNumber,
                    pageSize,
                    appliedLogsFilters!,
                    appliedLogsFilters.validationProcess
                );
                const newLogs = res.items;
                newLogs.forEach(log => {
                    const parts = log.additionalData?.split(";")!;
                    if (parts) {
                        log.user = parts[0];
                        log.calculationDate = parts[1].replace(" ", "T");
                        log.calculationType = parseInt(parts[2].split("=")[1]);
                    }
                });
                setLogs([...newLogs]);
                setLogsTotalCount(res.totalRecordCount);
            } catch (error) {
                toast("Task server not connected - unable to load logs", false, 2000);
            } finally {
                if (useLoading) setLoadingLogs(false);
            }
        },
        [pageNumber, pageSize, appliedLogsFilters]
    );

    const handleApplyFilters = () => {
        setAppliedLogsFilters(logsFilters);
    };

    const handleResetFilters = () => {
        setLogsFilters(logsFiltersInit);
        setAppliedLogsFilters(logsFiltersInit);
    };

    useEffect(() => {
        if (logsFilters.action === 2 && logsFilters.validationProcess !== null) {
            setLogsFilters({ ...logsFilters, validationProcess: ValidationProcess.Unknown });
        }
    }, [logsFilters]);

    useEffect(() => {
        fetchCalculations(true);
        const intervalId = setInterval(() => {
            fetchCalculations(false);
        }, 60000);
        return () => clearInterval(intervalId);
    }, [fetchCalculations]);

    return (
        <div id="logs-container-a" className="logs-container transition-container">
            <div id="logs-filters-a" className="logs-filters">
                <div id="logs-filters-inputs-a" className="logs-filters-inputs">
                    <div id="div-logs-a">
                        <p id="p-logs-calculationDate-a">Calculation date</p>
                        <CalendarInput
                            primary="calendarInput-logs-a"
                            timestampDate={logsFilters.calculationDate!}
                            handleTimestampDateChange={(e: any) =>
                                setLogsFilters({
                                    ...logsFilters,
                                    calculationDate: formatDate(e),
                                })
                            }
                        />
                    </div>
                    <div id="div-logs-b">
                        <p id="p-ulogs-user-a">User</p>
                        <Select
                            id="select-logs-a"
                            className="logs-filters-user-selector"
                            search
                            selectOnBlur={false}
                            clearable
                            value={logsFilters.user}
                            options={users}
                            onChange={(e, data: any) =>
                                setLogsFilters({
                                    ...logsFilters,
                                    user: data.value,
                                })
                            }
                        />
                    </div>
                    <div id="div-logs-c">
                        <p id="p-businessProcess-logs">Business Process</p>
                        <Select
                            id="select-logs-b"
                            disabled={logsFilters.action === 2}
                            selectOnBlur={false}
                            value={logsFilters.validationProcess}
                            options={validationProcessFilterOptions}
                            onChange={(e, data: any) => {
                                setLogsFilters({
                                    ...logsFilters,
                                    validationProcess: data.value,
                                });
                            }}
                        />
                    </div>
                    <div id="div-logs-d">
                        <p id="p-logs-started">Started</p>
                        <CalendarInput
                            primary="calendarInput-logs-b"
                            timestampDate={logsFilters.started!}
                            handleTimestampDateChange={(e: any) =>
                                setLogsFilters({
                                    ...logsFilters,
                                    started: formatDate(e),
                                })
                            }
                        />
                    </div>
                    <div id="div-logs-e">
                        <p id="p-logs-action">Action</p>
                        <Select
                            id="select-logsc"
                            className="logs-filters-action-selector"
                            selectOnBlur={false}
                            value={logsFilters.action}
                            options={calculationTypeOptions}
                            onChange={(e, data: any) =>
                                setLogsFilters({
                                    ...logsFilters,
                                    action: +data.value,
                                })
                            }
                        />
                    </div>
                    <div id="div-logs-status">
                        <p id="p-logs-status">Status</p>
                        <Select
                            id="select-logs-status"
                            className="logs-filters-action-selector"
                            selectOnBlur={false}
                            value={logsFilters.calculationStatus}
                            options={statusOptions}
                            onChange={(e, data: any) =>
                                setLogsFilters({
                                    ...logsFilters,
                                    calculationStatus: data.value,
                                })
                            }
                        />
                    </div>
                </div>
                <div id="div-logs-f" className="logs-filters-buttons">
                    <Button
                        id="button-logs-ApplyFilters"
                        primary
                        content="Apply filters"
                        onClick={handleApplyFilters}
                        disabled={logsFilters === logsFiltersInit}
                    />
                    <Button
                        id="button-logs-ResetFilters"
                        primary
                        content="Reset filters"
                        onClick={handleResetFilters}
                        disabled={logsFilters === logsFiltersInit}
                    />
                </div>
            </div>
            {loadingLogs ? (
                <Loading />
            ) : (
                <>
                    <Table id="table-logs-a" striped basic="very" stackable>
                        <Table.Header id="tableHeader-logs-a">
                            <Table.Row id="tableRow-logs-a">
                                <Table.HeaderCell id="tableHeaderCell-logs-no" collapsing>
                                    No
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-logs-calculationDate"
                                    collapsing
                                >
                                    Calculation date
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-user">
                                    User
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-started">
                                    Started
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-finished">
                                    Finished
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-businessProcess">
                                    Business Process
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-action">
                                    Action
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-subtasks">
                                    Timestamps (Failed/Success/Running/Total)
                                </Table.HeaderCell>
                                <Table.HeaderCell id="tableHeaderCell-logs-a"></Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body id="tableBody-logs-a">
                            {logs !== undefined &&
                                logs.map(log => <LogRow key={log.guid} log={log} />)}
                        </Table.Body>
                    </Table>
                    <div>
                        <Pagination
                            id="pagination-logs-a"
                            firstItem={{
                                content: (
                                    <Icon id="icon-logs-doubleLeft-a" name="angle double left" />
                                ),
                                icon: true,
                                disabled: pageNumber === 1,
                            }}
                            lastItem={{
                                content: (
                                    <Icon id="icon-logs-doubleRight-a" name="angle double right" />
                                ),
                                icon: true,
                                disabled: pageNumber === Math.ceil(logsTotalCount / pageSize),
                            }}
                            prevItem={{
                                content: <Icon id="icon-logs-angleLeft-a" name="angle left" />,
                                icon: true,
                                disabled: pageNumber === 1,
                            }}
                            nextItem={{
                                content: <Icon id="icon-logs-angleRight-a" name="angle right" />,
                                icon: true,
                                disabled: pageNumber === Math.ceil(logsTotalCount / pageSize),
                            }}
                            activePage={pageNumber}
                            totalPages={Math.ceil(logsTotalCount / pageSize)}
                            onPageChange={(e, data: any) => setPageNumber(+data.activePage)}
                        />
                        <Select
                            className="logs-page-size-select"
                            options={pageSizeOptions}
                            onChange={(e, data: any) => {
                                setPageNumber(1);
                                setPageSize(data.value);
                            }}
                            defaultValue={pageSizeOptions[0].value}
                            value={pageSize}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
